import type { IPersonAbsenceInfo, IShiftPersonLineRec } from "@/lib/backend/manufacturing2.generated.types";
import { deepClone, dateFromCalendarISODateString, dateFromDayShift, shiftNumber } from "@/lib/util";

type ShiftTag = string;
type PersonUuid = string;

type ShiftAbsence = {
    calendarIsoDate: string;
    dayShift: number;
};

/**
 * Calculates the shift tag for the given calendar ISO date string (`YYYY-MM-DD`) and day shift (0–2).
 */
export function getShiftTag({ calendarIsoDate, dayShift }: ShiftAbsence): ShiftTag {
    const calendarDate = dateFromCalendarISODateString(calendarIsoDate);
    const atShiftStart = dateFromDayShift(calendarDate, dayShift);
    return shiftNumber(atShiftStart).shift_tag;
}

/**
 * Groups absent person UUIDs by shift tag.
 * Every `shiftTag` in `shiftTags` is included in the map (with an empty array if no one is absent).
 */
export function groupAbsentPersonUuidsByShiftTag(
    shiftTags: ShiftTag[],
    peopleAbsences: IPersonAbsenceInfo[]
): Map<ShiftTag, PersonUuid[]> {
    const personUuidsByShiftTag = new Map<ShiftTag, Set<PersonUuid>>(
        shiftTags.map(shiftTag => [shiftTag, new Set<PersonUuid>()])
    );

    for (const { person_uuid, absences } of peopleAbsences) {
        for (const absence of absences) {
            for (const dayShift of absence.shifts) {
                const shiftTag = getShiftTag({ calendarIsoDate: absence.date, dayShift });
                personUuidsByShiftTag.get(shiftTag)?.add(person_uuid);
            }
        }
    }

    // Convert each Set of absent person UUIDs to a sorted array for consistent order, and return as a new Map.
    return new Map<ShiftTag, PersonUuid[]>(
        [...personUuidsByShiftTag.entries()].map(([shiftTag, set]) => [shiftTag, [...set].sort()])
    );
}

/**
 * Groups unique scheduled person UUIDs into a Set, keyed by `shift_tag`, across all lines.
 * @param lines - The list of schedule lines containing shifts and assigned people.
 */
export function groupScheduledPersonUuidsByShiftTag(lines: IShiftPersonLineRec[]): Map<ShiftTag, Set<PersonUuid>> {
    const result = new Map<ShiftTag, Set<PersonUuid>>();

    for (const line of lines) {
        for (const shift of line.shifts) {
            let personUuids = result.get(shift.shift_tag);

            if (!personUuids) {
                personUuids = new Set<PersonUuid>();
                result.set(shift.shift_tag, personUuids);
            }

            for (const person of shift.persons) {
                personUuids.add(person.uuid);
            }
        }
    }

    return result;
}

export type ShiftAbsenceSummary<TPerson extends { uuid: string }> = {
    absentPeopleByShiftTag: Map<ShiftTag, TPerson[]>;
    scheduledAbsentPeopleByShiftTag: Map<ShiftTag, TPerson[]>;
    totalAbsenceCount: number;
    scheduledAbsentPeopleCount: number;
};

/**
 * Summarizes absences per shift and aggregates cross-referenced data for absent people who are scheduled in those shifts.
 * @template TPerson - The type representing a person object, requiring at least a `uuid`.
 */
export function buildShiftAbsenceSummary<TPerson extends { uuid: PersonUuid }>(
    absentPeopleByShiftTag: Map<ShiftTag, TPerson[]>,
    scheduledPersonUuidsByShiftTag: Map<ShiftTag, Set<PersonUuid>>
): ShiftAbsenceSummary<TPerson> {
    const scheduledAbsentPeopleByShiftTag = new Map<ShiftTag, TPerson[]>();
    let totalAbsenceCount = 0;
    let scheduledAbsentPeopleCount = 0;

    for (const [shiftTag, absentPeople] of absentPeopleByShiftTag) {
        // Pull the Set out once before filtering to optimize performance
        const scheduledPersonUuids = scheduledPersonUuidsByShiftTag.get(shiftTag);

        // If no one is scheduled for this shift, we can skip filtering entirely
        if (!scheduledPersonUuids || scheduledPersonUuids.size === 0) {
            scheduledAbsentPeopleByShiftTag.set(shiftTag, []);
            totalAbsenceCount += absentPeople.length;
            continue;
        }

        const scheduledAbsentPeople = absentPeople.filter(person => scheduledPersonUuids.has(person.uuid));

        scheduledAbsentPeopleByShiftTag.set(shiftTag, scheduledAbsentPeople);
        totalAbsenceCount += absentPeople.length;
        scheduledAbsentPeopleCount += scheduledAbsentPeople.length;
    }

    return {
        absentPeopleByShiftTag,
        scheduledAbsentPeopleByShiftTag,
        totalAbsenceCount,
        scheduledAbsentPeopleCount,
    };
}

export type RemovePeopleFromShiftScheduleResult = {
    lines: IShiftPersonLineRec[];
    didRemovePeople: boolean;
};

/**
 * Removes specified individuals from matching shift schedules.
 * * @note This operation is immutable; it returns a deeply cloned copy of the schedule
 * if modifications occur, leaving the original `lines` array untouched.
 * * @returns An object containing the updated schedule lines and a `didRemovePeople` flag
 * indicating whether any structural changes actually took place.
 */
export function removePeopleFromShiftSchedule(
    lines: IShiftPersonLineRec[],
    personUuidsByShiftTag: Map<ShiftTag, Set<PersonUuid>>
): RemovePeopleFromShiftScheduleResult {
    // Early escape: If the map is completely empty, zero mutations can happen.
    if (personUuidsByShiftTag.size === 0) {
        return { lines, didRemovePeople: false };
    }

    const updatedLines: IShiftPersonLineRec[] = deepClone(lines);
    let didRemovePeople = false;

    for (const line of updatedLines) {
        for (const shift of line.shifts) {
            const personUuids = personUuidsByShiftTag.get(shift.shift_tag);

            // Skip shifts where there are no target deletions, or the shift itself is empty
            if (!personUuids || personUuids.size === 0 || shift.persons.length === 0) {
                continue;
            }

            const previousPersonCount = shift.persons.length;
            shift.persons = shift.persons.filter(person => !personUuids.has(person.uuid));

            if (shift.persons.length !== previousPersonCount) {
                didRemovePeople = true;
            }
        }
    }

    return {
        lines: updatedLines,
        didRemovePeople,
    };
}
